import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectParticipantAttendedModules } from '../../../../store/courseSelectors';
import { selectSortedBMIEntries } from '../../../../store/participantSelectors';
import { clearBmiEntries, deleteBMI, loadBMIEntries } from '../../../../store/participantService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import {
    BLOOD_TEST_EVENTS_ERROR,
    BMI_ENTRIES_ERROR,
    MEASUREMENT_TYPES_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import BMIRow from './BMIRow';
import EditBMIEntry from './EditBMIEntry';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'eventDate', numeric: false, label: 'Date' },
    { id: 'height', numeric: true, label: 'Height (cm)' },
    { id: 'weight', numeric: true, label: 'Weight (kg)' },
    { id: 'lost', numeric: true, label: '% Lost' },
    { id: 'bmi', numeric: true, label: 'BMI' },
    { id: 'measurement', numeric: false, label: 'Measurement' },
    { id: 'event', numeric: false, label: 'Event' },
    { id: 'linkedModule', numeric: false, label: 'Linked Module' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'eventDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const BMI = ({
    participant,
    isEditDisabled = true,
    isDeleteDisabled = true,
    isCreateDisabled = true,
    isAuditable,
    onSearchAuditItems,
    linkedModuleId
}) => {
    const dispatch = useDispatch();
    const bmiRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [modules, setModules] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const attendedModules = useSelector(selectParticipantAttendedModules);
    const bmiEntries = useSelector(selectSortedBMIEntries);
    const bmiEntriesMetaData = useSelector(
        (state) => state.entities.participantService.bmiEntriesMetaData
    );
    const measurementTypes = useSelector(
        (state) => state.entities.directusService.measurementTypes
    );
    const bloodTestEvents = useSelector((state) => state.entities.directusService.bloodTestEvents);

    // USEEFFECTS
    useEffect(() => {
        return () => cleanUp();
    }, []);

    useEffect(() => {
        participant?.id && dispatch(loadBMIEntries(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (bloodTestEvents?.length < 1 || measurementTypes?.length < 1) return;
        if (bmiEntriesMetaData?.loaded) {
            let modules = [];
            if (!bmiEntries?.length) {
                setRows([]);
                if (attendedModules?.length) modules = [...attendedModules];
                setLoadingContentMessage(BMI_ENTRIES_ERROR);
            } else {
                const rows = bmiEntries.map((el, i, arr) => ({
                    ...el,
                    lost: arr[i - 1]
                        ? (((arr[i - 1].weight - el.weight) / arr[i - 1].weight) * 100).toFixed(1)
                        : 0,
                    bmi: ((el.weight / (el.height * el.height)) * 10000).toFixed(1) || 0,
                    measurement: getNameFromId(measurementTypes, el?.measurementId),
                    event: getNameFromId(bloodTestEvents, el?.eventId),
                    linkedModule: el.linkedModuleId
                        ? attendedModules?.find((entry) => entry.id === el.linkedModuleId)?.title
                        : undefined
                }));
                setRows(rows);

                if (attendedModules?.length) {
                    modules = attendedModules.filter(
                        (el) => !bmiEntries.find((entry) => entry.linkedModuleId === el.id)
                    );
                }
            }
            setModules(modules.map((el) => ({ ...el, name: el.title })) || []);
        }
    }, [bmiEntries, attendedModules, bmiEntriesMetaData, measurementTypes, bloodTestEvents]);

    // FNs
    const loadingError = () => {
        if (measurementTypes?.length < 1) return MEASUREMENT_TYPES_ERROR;
        if (bloodTestEvents?.length < 1) return BLOOD_TEST_EVENTS_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <BMIRow
                key={el.id}
                row={el}
                isDisabled={isEditDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                isAuditable={isAuditable}
                onSearchAuditItems={onSearchAuditItems}
            />
        ));

    const cleanUp = () => {
        dispatch(clearBmiEntries());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setShowForm(true);
        setSelectedRow({});
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteBMI(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(bmiRef);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg)
        return (
            <div className={`${local.wellBeingSection} ${app.fadein}`}>
                <LoadingSpinner content={errorMsg} />
            </div>
        );

    // RENDER
    return (
        <div className={local.wellBeingSection} data-testid="bmi" ref={bmiRef}>
            <h2 className={app.sectionHeading}>BMI</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm && (
                    <Button
                        id="addBMIButton"
                        type="button"
                        disabled={isCreateDisabled}
                        content="Add New BMI"
                        icon={true}
                        onClick={onCreate}
                    />
                )}
                <div>
                    {showForm && (
                        <EditBMIEntry
                            participant={participant}
                            onClose={onClose}
                            isDisabled={
                                (formType === 'edit' && isEditDisabled) ||
                                (formType === 'create' && isCreateDisabled)
                            }
                            attendedModules={modules}
                            linkedModuleId={linkedModuleId || ''}
                            formType={formType}
                            row={selectedRow}
                            lastRow={rows.length ? rows[rows.length - 1] : {}}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

BMI.propTypes = {
    participant: PropTypes.object.isRequired,
    isEditDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    isCreateDisabled: PropTypes.bool,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func,
    linkedModuleId: PropTypes.string
};

export default BMI;
